import { useState } from "react";
import { History, Search, Download } from "lucide-react";
import { useProducts, useBranches, useKardex, MOVEMENT_TYPE_LABEL, InventoryMovementType, PRODUCT_UNIT_LABEL } from "@/api/inventory";
import { downloadFile } from "@/api/client";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" });
}

const MOVEMENT_TONE: Record<InventoryMovementType, string> = {
  COMPRA: "text-green-700",
  VENTA: "text-red-600",
  AJUSTE: "text-amber-700",
  INICIAL: "text-gray-600",
  ANULACION: "text-blue-600",
  TRANSFERENCIA_SALIDA: "text-red-600",
  TRANSFERENCIA_ENTRADA: "text-green-700",
  DEVOLUCION: "text-blue-600",
  MERMA: "text-orange-600",
  DEVOLUCION_PROVEEDOR: "text-orange-600",
};

const MOVEMENT_TYPE_OPTIONS = Object.keys(MOVEMENT_TYPE_LABEL) as InventoryMovementType[];

/**
 * Kardex: historial de movimientos de inventario de un producto, con el
 * saldo corriente (`balanceAfter`) tras cada movimiento. Reutiliza
 * `InventoryMovement` — el mismo dato que ya alimentaba el panel lateral de
 * un producto — pero como pantalla dedicada, con filtro de sucursal y rango
 * de fechas, pensada para auditar "qué le pasó a este producto" de un
 * vistazo.
 */
export default function KardexPage() {
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState<string | null>(null);
  const [branchId, setBranchId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [movementType, setMovementType] = useState("");

  const { data: productsResponse } = useProducts({ search: search || undefined, pageSize: 20 });
  const products = productsResponse?.data ?? [];
  const { data: branches } = useBranches();
  const kardexFilters = { branchId: branchId || undefined, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, movementType: movementType || undefined };
  const { data: kardex, isLoading } = useKardex(productId, kardexFilters);

  const handleExport = async (format: "csv" | "xlsx") => {
    if (!productId || !kardex) return;
    await downloadFile(`/inventory/products/${productId}/kardex.${format}`, `kardex-${kardex.product.sku}.${format}`, kardexFilters as Record<string, string | undefined>);
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Kardex</h1>
        <p className="text-sm text-gray-500">Historial completo de movimientos de un producto: compras, ventas, ajustes, traslados y anulaciones, con el saldo tras cada uno.</p>
      </div>

      <div className="card space-y-3 p-4">
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input-field pl-9"
            placeholder="Buscar producto por nombre, SKU o código de barras..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setProductId(null);
            }}
          />
        </div>
        {search && !productId && (
          <div className="max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-100">
            {products.map((p) => (
              <button
                key={p.id}
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50"
                onClick={() => {
                  setProductId(p.id);
                  setSearch(`${p.sku} — ${p.name}`);
                }}
              >
                <span className="text-gray-900">{p.name}</span>
                <span className="text-xs text-gray-400">{p.sku}</span>
              </button>
            ))}
            {products.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">Sin resultados.</p>}
          </div>
        )}

        {productId && (
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 pt-3">
            {branches && branches.length > 1 && (
              <select className="input-field w-auto" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Todas las sucursales</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}
            <select className="input-field w-auto" value={movementType} onChange={(e) => setMovementType(e.target.value)}>
              <option value="">Todos los tipos de movimiento</option>
              {MOVEMENT_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {MOVEMENT_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Desde</span>
              <input type="date" className="input-field w-auto" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <span>hasta</span>
              <input type="date" className="input-field w-auto" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button type="button" className="btn-secondary px-2.5 py-1.5 text-xs" onClick={() => handleExport("csv")} disabled={!kardex}>
                <Download size={13} className="mr-1" /> CSV
              </button>
              <button type="button" className="btn-secondary px-2.5 py-1.5 text-xs" onClick={() => handleExport("xlsx")} disabled={!kardex}>
                <Download size={13} className="mr-1" /> Excel
              </button>
            </div>
          </div>
        )}
      </div>

      {!productId && (
        <div className="card flex flex-col items-center gap-2 p-10 text-center text-gray-400">
          <History size={28} />
          <p className="text-sm">Busca y selecciona un producto arriba para ver su kardex.</p>
        </div>
      )}

      {productId && (
        <div className="card overflow-hidden">
          {kardex && (
            <div className="flex items-center justify-between border-b border-gray-100 p-4">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {kardex.product.name} <span className="font-normal text-gray-400">({kardex.product.sku})</span>
                </p>
                <p className="text-xs text-gray-500">
                  Saldo total (todas las sucursales): {kardex.product.stockQty} {PRODUCT_UNIT_LABEL[kardex.product.unit]}
                </p>
              </div>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2.5">Fecha</th>
                <th className="px-4 py-2.5">Documento</th>
                <th className="px-4 py-2.5">Tipo</th>
                <th className="px-4 py-2.5">Sucursal</th>
                <th className="px-4 py-2.5 text-right">Entrada</th>
                <th className="px-4 py-2.5 text-right">Salida</th>
                <th className="px-4 py-2.5 text-right">Saldo</th>
                <th className="px-4 py-2.5 text-right">Costo unitario</th>
                <th className="px-4 py-2.5 text-right">Costo total</th>
                <th className="px-4 py-2.5">Usuario</th>
                <th className="px-4 py-2.5">Notas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && (
                <tr>
                  <td colSpan={11} className="px-4 py-6 text-center text-gray-400">
                    Cargando...
                  </td>
                </tr>
              )}
              {kardex?.movements.map((m) => {
                const qty = Number(m.quantity);
                return (
                  <tr key={m.id}>
                    <td className="px-4 py-2 text-gray-600">{formatDateTime(m.createdAt)}</td>
                    <td className="px-4 py-2 text-gray-500">
                      {m.referenceType ? `${m.referenceType}${m.referenceId ? ` #${m.referenceId.slice(0, 8)}` : ""}` : "—"}
                    </td>
                    <td className={`px-4 py-2 font-medium ${MOVEMENT_TONE[m.movementType]}`}>{MOVEMENT_TYPE_LABEL[m.movementType]}</td>
                    <td className="px-4 py-2 text-gray-600">{m.branch?.name ?? "—"}</td>
                    <td className="px-4 py-2 text-right text-green-700">{qty > 0 ? qty : ""}</td>
                    <td className="px-4 py-2 text-right text-red-600">{qty < 0 ? Math.abs(qty) : ""}</td>
                    <td className="px-4 py-2 text-right text-gray-900">{m.balanceAfter}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{m.unitCost != null ? currency.format(Number(m.unitCost)) : "—"}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{m.totalCost != null ? currency.format(Number(m.totalCost)) : "—"}</td>
                    <td className="px-4 py-2 text-gray-500">{m.createdBy?.fullName ?? "—"}</td>
                    <td className="px-4 py-2 text-gray-500">{m.notes ?? "—"}</td>
                  </tr>
                );
              })}
              {kardex && kardex.movements.length === 0 && !isLoading && (
                <tr>
                  <td colSpan={11} className="px-4 py-6 text-center text-gray-400">
                    Sin movimientos registrados en este rango.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
